import React, { useEffect, useMemo, useState } from "react";
import config from "../config.js";
import pinPrompt from "../utils/pinPrompt";
import { detailToString } from '../utils/apiError';

/**
 * Cut a new revision of a purchased component.
 *
 * Where the parts modal asks "copy files to the new revision?", this asks the
 * question that actually matters for something we BUY: what does this change
 * do to the pieces already on the shelf and already on order?
 *
 *   clarification    wording or drawing cleanup — existing pieces unaffected
 *   interchangeable  the spec moved but existing pieces are still usable
 *   breaking         existing pieces are NOT usable
 *
 * "Breaking" is disabled outright while anything is reserved, because that
 * work is already committed to the old spec. The API refuses it too — this is
 * the same rule said early, so the operator does not fill in a form to be told
 * no at the end.
 */
export default function NewComponentRevisionModal({ component, documents, onClose, onCreated }) {
  const API_BASE = config.API_BASE_URL;

  const [stockImpact, setStockImpact] = useState("clarification");
  const [description, setDescription] = useState("");
  const [requestedBy, setRequestedBy] = useState("internal");
  const [disposition, setDisposition] = useState("");
  const [pricingImpact, setPricingImpact] = useState("");
  const [pricingRef, setPricingRef] = useState("");
  const [revisionCode, setRevisionCode] = useState("");
  const [retireIds, setRetireIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [openJobs, setOpenJobs] = useState([]);

  const onHand = component?.stock_quantity ?? 0;
  const reserved = component?.reserved_quantity ?? 0;
  const breakingBlocked = reserved > 0;

  const liveDocs = useMemo(() => (documents || []).filter((d) => !d.is_retired), [documents]);

  useEffect(() => {
    if (breakingBlocked && stockImpact === "breaking") setStockImpact("clarification");
  }, [breakingBlocked, stockImpact]);

  useEffect(() => {
    let cancelled = false;
    async function loadJobs() {
      if (!breakingBlocked || !component?.consumable_id) return;
      try {
        const res = await fetch(
          `${API_BASE}/components/${component.consumable_id}/open_order_exposure`
        );
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setOpenJobs(data.assembly_bom_rows || []);
      } catch {
        /* the hint is optional; the count above is the real signal */
      }
    }
    loadJobs();
    return () => {
      cancelled = true;
    };
  }, [API_BASE, breakingBlocked, component]);

  function toggleRetire(id) {
    setRetireIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");

    if (stockImpact === "breaking" && onHand > 0 && !disposition.trim()) {
      setError(
        `${onHand} are on the shelf and this change makes them unusable. Say what happens to them.`
      );
      return;
    }

    const pin = await pinPrompt("PIN to cut this revision:");
    if (!pin) return;

    setBusy(true);
    try {
      const body = {
        pin,
        stock_impact: stockImpact,
        change_description: description.trim() || null,
        change_requested_by: requestedBy || null,
      };
      if (stockImpact === "breaking" && disposition.trim())
        body.stock_disposition = disposition.trim();
      if (pricingImpact) body.pricing_impact = pricingImpact;
      if (pricingRef.trim()) body.pricing_quote_ref = pricingRef.trim();
      if (revisionCode.trim()) body.revision_code = revisionCode.trim();
      if (retireIds.length) body.retire_document_ids = retireIds;

      const res = await fetch(`${API_BASE}/components/${component.consumable_id}/revisions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(detailToString(data.detail) || "Could not create the revision.");
      onCreated(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  const impacts = [
    {
      value: "clarification",
      title: "Clarification",
      blurb: "Wording or drawing cleanup. Nothing about the physical part changed.",
    },
    {
      value: "interchangeable",
      title: "Interchangeable",
      blurb: "The spec moved, but pieces built to the old one are still usable.",
    },
    {
      value: "breaking",
      title: "Breaking",
      blurb: "Pieces built to the old spec are NOT usable.",
    },
  ];

  return (
    <div className="modal show d-block" style={{ backgroundColor: "rgba(0,0,0,.5)", zIndex: 1055 }}>
      <div className="modal-dialog modal-lg modal-dialog-scrollable">
        <div className="modal-content">
          <form onSubmit={handleSubmit}>
            <div className="modal-header">
              <h5 className="modal-title">
                New revision — {component?.product_id}
                {component?.current_revision_code && (
                  <span className="text-muted ms-2 fs-6">
                    Rev {component.current_revision_code} →{" "}
                    {revisionCode.trim() || "next"}
                  </span>
                )}
              </h5>
              <button type="button" className="btn-close" onClick={onClose} />
            </div>

            <div className="modal-body">
              {error && <div className="alert alert-danger py-2">{error}</div>}

              <div className="d-flex gap-3 mb-3 small">
                <span className="badge bg-light text-dark border">On hand: {onHand}</span>
                <span className={`badge border ${reserved > 0 ? "bg-warning text-dark" : "bg-light text-dark"}`}>
                  Reserved: {reserved}
                </span>
              </div>

              <label className="form-label fw-semibold">What does this change do to existing pieces?</label>
              <div className="mb-3">
                {impacts.map((imp) => {
                  const disabled = imp.value === "breaking" && breakingBlocked;
                  return (
                    <div className="form-check" key={imp.value}>
                      <input
                        className="form-check-input"
                        type="radio"
                        name="stockImpact"
                        id={`si-${imp.value}`}
                        value={imp.value}
                        checked={stockImpact === imp.value}
                        disabled={disabled}
                        onChange={(e) => setStockImpact(e.target.value)}
                      />
                      <label
                        className={`form-check-label ${disabled ? "text-muted" : ""}`}
                        htmlFor={`si-${imp.value}`}
                      >
                        <strong>{imp.title}</strong> — {imp.blurb}
                      </label>
                    </div>
                  );
                })}
              </div>

              {breakingBlocked && (
                <div className="alert alert-warning py-2 small">
                  <strong>Breaking is unavailable:</strong> {reserved} of {component?.product_id} are
                  reserved against open work, which is already committed to the current spec.
                  {openJobs.length > 0 && (
                    <div className="mt-1">
                      Consumed by:{" "}
                      {openJobs
                        .slice(0, 6)
                        .map((a) => a.assembly_name || `assembly #${a.assembly_id}`)
                        .join(", ")}
                      .
                    </div>
                  )}
                  <div className="mt-1">
                    Finish or cancel that work first — or issue a new SKU if the old and new parts
                    must coexist on the shelf.
                  </div>
                </div>
              )}

              {stockImpact === "breaking" && onHand > 0 && (
                <div className="mb-3">
                  <label className="form-label">
                    What happens to the {onHand} on the shelf? <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    rows={2}
                    value={disposition}
                    onChange={(e) => setDisposition(e.target.value)}
                    placeholder="e.g. Scrap them — Boyd is remaking at their cost. Quarantined on rack 4."
                  />
                </div>
              )}

              <div className="mb-3">
                <label className="form-label">What changed, and why?</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="e.g. Hole diameter .375 → .406 so a 3/8 bolt actually fits. Pitch unchanged."
                />
              </div>

              <div className="row g-3 mb-3">
                <div className="col-md-4">
                  <label className="form-label">Requested by</label>
                  <select
                    className="form-select"
                    value={requestedBy}
                    onChange={(e) => setRequestedBy(e.target.value)}
                  >
                    <option value="internal">Us</option>
                    <option value="vendor">The vendor</option>
                    <option value="customer">The customer</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Price</label>
                  <select
                    className="form-select"
                    value={pricingImpact}
                    onChange={(e) => setPricingImpact(e.target.value)}
                  >
                    <option value="">Not stated</option>
                    <option value="unchanged">Unchanged</option>
                    <option value="revised">Revised</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label">Quote ref</label>
                  <input
                    className="form-control"
                    value={pricingRef}
                    onChange={(e) => setPricingRef(e.target.value)}
                    placeholder="Boyd Q-4471"
                  />
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">
                  Revision code <span className="text-muted small">(blank = next in sequence)</span>
                </label>
                <input
                  className="form-control"
                  style={{ maxWidth: 160 }}
                  value={revisionCode}
                  onChange={(e) => setRevisionCode(e.target.value)}
                  placeholder="auto"
                />
                <div className="form-text">
                  Set this to match the vendor's own marking when they stamp a different letter on
                  their print — receiving should not have to translate.
                </div>
              </div>

              {liveDocs.length > 0 && (
                <div>
                  <label className="form-label fw-semibold">
                    Retire any drawing this change supersedes
                  </label>
                  <div className="form-text mb-2">
                    Retired files stay readable on the revisions they applied to. Nothing is deleted.
                  </div>
                  {liveDocs.map((d) => (
                    <div className="form-check" key={d.component_document_id}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id={`retire-${d.component_document_id}`}
                        checked={retireIds.includes(d.component_document_id)}
                        onChange={() => toggleRetire(d.component_document_id)}
                      />
                      <label className="form-check-label" htmlFor={`retire-${d.component_document_id}`}>
                        {d.display_name}{" "}
                        <span className="text-muted small">({d.original_filename})</span>
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                Cancel
              </button>
              <button className="btn btn-dark" disabled={busy}>
                {busy ? "Cutting…" : "Cut revision"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
